/**
 * Longer-running movements: eight weekly buckets, so a single good or bad day
 * cannot look like a trend. Every figure comes from the same demo engine the
 * Overview and Alerts read, so a trend and a tile can never disagree.
 */

import type { Trend, TrendDirection } from '@/api/types'
import { formatChange, formatCurrency, formatPercent, formatRatio, percentChange } from '@/design/format'
import { addDays, type DateWindow, type GlobalFilters } from '@/lib/filters'

import { round2, scopeFor, type DemoDataset } from '../generators/engine'

const WEEKS = 8
const FLAT_THRESHOLD = 0.03

function weeklyWindows(today: string): DateWindow[] {
  return Array.from({ length: WEEKS }, (_, index) => {
    const weeksAgo = WEEKS - 1 - index
    const to = addDays(today, -7 * weeksAgo)
    return { from: addDays(to, -6), to, days: 7 }
  })
}

function average(values: readonly number[]): number {
  return values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : 0
}

/** Compares the average of the first half of the weeks with the second half, to smooth out a single noisy week. */
function directionFor(values: readonly number[]): { direction: TrendDirection; change: number | null } {
  const half = Math.floor(values.length / 2)
  if (half === 0) return { direction: 'flat', change: null }
  const change = percentChange(average(values.slice(-half)), average(values.slice(0, half)))
  if (change === null || Math.abs(change) < FLAT_THRESHOLD) return { direction: 'flat', change }
  return { direction: change > 0 ? 'up' : 'down', change }
}

function changeLabel(change: number | null): string {
  return change === null ? `Stable over the last ${WEEKS} weeks` : `${formatChange(change)} over the last ${WEEKS} weeks`
}

export function buildTrends(dataset: DemoDataset, filters: GlobalFilters): Trend[] {
  const { tenant, today } = dataset
  const money = { currency: tenant.currency, locale: tenant.locale }
  const scope = scopeFor(tenant, filters)
  const weeks = weeklyWindows(today).map((window) => dataset.period(window, scope))
  const first = <T,>(values: readonly T[]) => values[0] as T
  const last = <T,>(values: readonly T[]) => values[values.length - 1] as T

  const revenue = weeks.map((week) => round2(week.revenue))
  const revenueTrend = directionFor(revenue)

  const conversion = weeks.map((week) => (week.stages.visitors > 0 ? week.orders / week.stages.visitors : 0))
  const conversionTrend = directionFor(conversion)

  const roas = weeks.map((week) => (week.adSpend > 0 ? week.attributedRevenue / week.adSpend : 0))
  const roasTrend = directionFor(roas)

  const newShare = weeks.map((week) => {
    const total = week.newCustomers + week.returningCustomers
    return total > 0 ? week.newCustomers / total : 0
  })
  const newShareTrend = directionFor(newShare)

  return [
    {
      id: 'trend_revenue',
      title: 'Revenue',
      category: 'revenue',
      direction: revenueTrend.direction,
      changeLabel: changeLabel(revenueTrend.change),
      explanation: `Weekly revenue moved from ${formatCurrency(first(revenue), money)} to ${formatCurrency(last(revenue), money)} over the last ${WEEKS} weeks.`,
      entity: null,
      sparkline: revenue,
      drillDown: { path: '/analytics/revenue' },
    },
    {
      id: 'trend_conversion',
      title: 'Conversion rate',
      category: 'conversion',
      direction: conversionTrend.direction,
      changeLabel: changeLabel(conversionTrend.change),
      explanation: `Visitors converting to a purchase moved from ${formatPercent(first(conversion))} to ${formatPercent(last(conversion))} over the last ${WEEKS} weeks.`,
      entity: null,
      sparkline: conversion,
      drillDown: { path: '/analytics/funnel' },
    },
    {
      id: 'trend_roas',
      title: 'Blended ROAS',
      category: 'advertising',
      direction: roasTrend.direction,
      changeLabel: changeLabel(roasTrend.change),
      explanation: `Return on ad spend across every channel moved from ${formatRatio(first(roas))} to ${formatRatio(last(roas))} over the last ${WEEKS} weeks.`,
      entity: null,
      sparkline: roas,
      drillDown: { path: '/marketing/advertising' },
    },
    {
      id: 'trend_new_customers',
      title: 'New customer share',
      category: 'customer',
      direction: newShareTrend.direction,
      changeLabel: changeLabel(newShareTrend.change),
      explanation: `New customers' share of all customers moved from ${formatPercent(first(newShare))} to ${formatPercent(last(newShare))} over the last ${WEEKS} weeks.`,
      entity: null,
      sparkline: newShare,
      drillDown: { path: '/analytics/customers' },
    },
  ]
}
